import { StyleSheet, Dimensions, ViewStyle } from 'react-native';
import { ShadowConfig, LayoutMode } from './DbItemCard.types';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

/** 默认常量 */
export const ITEM_CARD_DEFAULTS = {
    numColumns: 2,
    itemSpacing: 8,
    rowSpacing: 8,
    sideSpacing: 12,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    cardHeight: 180,
    cardWidthHorizontal: 140,
    activeOpacity: 0.7,
    padding: 0,
};

/** 阴影配置 */
export const SHADOW_CONFIGS: Record<'none' | 'sm' | 'md' | 'lg', ShadowConfig> = {
    none: {
        shadowColor: 'transparent',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0,
        shadowRadius: 0,
        elevation: 0,
    },
    sm: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.08,
        shadowRadius: 2,
        elevation: 1,
    },
    md: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
    },
    lg: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 6,
    },
};

/** 计算卡片宽度 */
export const calculateCardWidth = (
    numColumns: number,
    itemSpacing: number,
    sideSpacing: number,
    screenWidth: number = SCREEN_WIDTH
): number => {
    // 总可用宽度 = 屏幕宽度 - 两侧边距
    const totalAvailableWidth = screenWidth - sideSpacing * 2;
    // 列之间的总间距 = (列数 - 1) * itemSpacing
    const totalColumnSpacing = (numColumns - 1) * itemSpacing;
    // 单卡片宽度 = (总可用宽度 - 列间距) / 列数
    return (totalAvailableWidth - totalColumnSpacing) / numColumns;
};

/** 判断是否为一行的最后一个 */
export const isRowLastItem = (index: number, numColumns: number): boolean => {
    return (index + 1) % numColumns === 0;
};

/** 判断是否为一行的第一个 */
export const isRowFirstItem = (index: number, numColumns: number): boolean => {
    return index % numColumns === 0;
};

/** 获取卡片边距样式 */
export const getCardMarginStyle = (
    index: number,
    numColumns: number,
    itemSpacing: number,
    rowSpacing: number,
    layoutMode: LayoutMode = 'grid'
): ViewStyle => {
    const isRowLast = isRowLastItem(index, numColumns);
    const isRowFirst = isRowFirstItem(index, numColumns);
    
    if (layoutMode === 'horizontal') {
        return {
            marginRight: itemSpacing,
        };
    }
    
    if (layoutMode === 'list' || numColumns === 1) {
        return {
            marginBottom: rowSpacing,
        };
    }
    
    // grid/waterfall 模式
    return {
        marginRight: isRowLast ? 0 : itemSpacing,
        marginBottom: rowSpacing,
    };
};

/** 基础样式 */
export const styles = StyleSheet.create({
    // 卡片容器
    card: {
        overflow: 'hidden',
    },
    
    // 卡片内容
    cardContent: {
        flex: 1,
    },
    
    // 禁用状态
    disabled: {
        opacity: 0.5,
    },
    
    // ========== 列表容器样式 ==========
    
    // 网格容器
    gridContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    
    // 瀑布流容器
    waterfallContainer: {
        flexDirection: 'row',
    },
    
    // 瀑布流列
    waterfallColumn: {
        flex: 1,
    },
    
    // 瀑布流滚动容器
    waterfallScrollContainer: {
        flexGrow: 1,
    },
    
    // 水平滚动容器
    horizontalContainer: {
        flexDirection: 'row',
    },
    
    // 列表容器
    listContainer: {
        flexDirection: 'column',
    },
    
    // 滚动容器
    scrollContainer: {
        flexGrow: 1,
    },
});
